import Phaser from 'phaser';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  FLOW_SPEED_BASE,
  DIFFICULTY,
  SPAWN_ATTEMPT_BASE_INTERVAL,
  SCORE,
  ENTITY_RADIUS,
  PARTICLES,
  POWER_SLOW_MULT,
  DEBUG,
  PLAYER,
  ENTITY_CAP,
  PERFORMANCE,
  FEEL,
  SHIELD,
  WBC_SPAWN_CAP,
  DECOY,
  PLAYER_BASE_RADIUS,
  HITBOX,
} from '../config/gameConfig.js';
import { circlesOverlap } from '../utils/collision.js';
import { Player } from '../entities/Player.js';
import { EntityPools } from '../systems/EntityPools.js';
import { Spawner, EntityKind } from '../systems/Spawner.js';
import { GameUI } from '../ui/GameUI.js';
import { mountGameOverDom } from '../ui/gameOverOverlay.js';
import { registerGameAtlas, Frames } from '../utils/atlas.js';
import {
  advanceMembranePhase,
  membraneScaleXY,
  wbcMembraneRotation,
} from '../utils/membraneVisual.js';
import { buildBloodstreamBackdrop } from '../utils/bloodstreamBackdrop.js';
import * as Move from '../entities/movement.js';
import { sfxCollect, sfxHit, sfxPower, resumeAudio } from '../audio/beeps.js';
import { debugState } from '../utils/debugFlags.js';

export class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Game' });
  }

  create() {
    resumeAudio();
    this.time.timeScale = 1;

    if (!this.textures.exists('game-atlas')) {
      registerGameAtlas(this);
    }

    const cam = this.cameras.main;
    cam.setBounds(0, 0, GAME_WIDTH, GAME_HEIGHT);
    cam.setZoom(1);
    cam.setScroll(0, 0);

    this._slowMoFxLatch = false;
    /** @type {Phaser.FX.ColorMatrix | undefined} */
    this._slowMoCm = undefined;
    this._fpsSmooth = 60;

    buildBloodstreamBackdrop(this);

    this.elapsed = 0;
    this.difficulty = 1;
    this.flowSpeed = FLOW_SPEED_BASE;
    this.score = 0;
    this.spawnAccumulator = 0;

    this.breakdown = {
      survival: 0,
      growth: 0,
      pickups: 0,
    };

    this.pools = new EntityPools(this);
    this.spawner = new Spawner(this.pools);
    this.player = new Player(this, GAME_WIDTH * 0.28, GAME_HEIGHT * 0.5);

    this.ui = new GameUI(this);

    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys('W,A,S,D,G');
    this.shiftKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
    this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    /** @type {{ sprite: Phaser.GameObjects.Image; vx: number; vy: number }[]} */
    this.decoys = [];
    this.cloneCooldownRemaining = 0;
    /** @type {number} Earliest `elapsed` (s) when another WBC may be selected to spawn */
    this.nextWbcSpawnAllowedAfter = 0;

    this.slowMoTimer = 0;
    /** True once death triggers transition out (blocks extra collision passes / scoring). */
    this._gameOverLeaving = false;
    /** Previous-frame decoy count (purge WBC when wave ends naturally). */
    this._prevDecoyCount = 0;

    this.seedParticles();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.destroyDecoys();
      this.time.timeScale = 1;
      this.applySlowMoCam(false);
      if (this.ui) this.ui.setSlowMoFx(false);
    });
  }

  seedParticles() {
    const cap = PARTICLES.maxActive;
    for (let i = 0; i < cap; i++) {
      const img = this.pools.particles.acquire(
        Math.random() * GAME_WIDTH,
        Math.random() * GAME_HEIGHT,
        1
      );
      img.setAlpha(PARTICLES.alpha);
      img.setScale(0.9 + Math.random() * 0.5);
      img.poolData.speed = PARTICLES.speedMin + Math.random() * (PARTICLES.speedMax - PARTICLES.speedMin);
    }
  }

  toggleDebug() {
    debugState.enabled = !debugState.enabled;
  }

  /**
   * Soft desaturate via camera ColorMatrix when supported (paired with vignette wash).
   * @param {boolean} enable
   */
  applySlowMoCam(enable) {
    const cam = this.cameras.main;
    const pf = cam.postFX;
    if (!pf || typeof pf.addColorMatrix !== 'function') return;

    if (enable) {
      if (!this._slowMoCm) {
        this._slowMoCm = pf.addColorMatrix();
        const cm = this._slowMoCm;
        if (typeof cm.desaturate === 'function') {
          cm.desaturate(0.52);
        } else if (typeof cm.grayscale === 'function') {
          cm.grayscale(0.38);
        } else if (typeof cm.saturate === 'function') {
          cm.saturate(-0.42);
        }
      }
    } else if (this._slowMoCm) {
      pf.remove(this._slowMoCm);
      this._slowMoCm = undefined;
    }
  }

  update(_t, deltaMs) {
    if (this._gameOverLeaving) {
      return;
    }

    let dt =
      typeof deltaMs === 'number' && Number.isFinite(deltaMs) && deltaMs > 0
        ? deltaMs / 1000
        : null;
    if (dt == null) {
      const clockDt = this.time.delta;
      dt =
        typeof clockDt === 'number' && Number.isFinite(clockDt) && clockDt > 0
          ? clockDt / 1000
          : 1 / 60;
    }
    dt = Math.min(dt, 0.12);

    this.elapsed += dt;
    const minutes = this.elapsed / 60;

    this.difficulty = 1 + this.elapsed * 0.035 + minutes * DIFFICULTY.entityPressurePerMinute * 6;
    this.flowSpeed = FLOW_SPEED_BASE + minutes * DIFFICULTY.flowBonusPerMinute;

    if (Phaser.Input.Keyboard.JustDown(this.keys.D) && this.shiftKey.isDown) {
      this.toggleDebug();
    }

    if (Phaser.Input.Keyboard.JustDown(this.keys.G) && this.shiftKey.isDown) {
      debugState.godMode = !debugState.godMode;
      console.log('[VIRUTOPIA] godMode', debugState.godMode);
    }

    if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) {
      this.tryActivateClonePower();
    }

    if (this.cloneCooldownRemaining > 0) {
      this.cloneCooldownRemaining = Math.max(0, this.cloneCooldownRemaining - dt);
    }

    if (this.slowMoTimer > 0) {
      this.slowMoTimer -= dt;
    }
    const inSlowMo = this.slowMoTimer > 0;
    this.time.timeScale = inSlowMo ? POWER_SLOW_MULT : 1;

    if (inSlowMo !== this._slowMoFxLatch) {
      this._slowMoFxLatch = inSlowMo;
      this.ui.setSlowMoFx(inSlowMo);
      this.applySlowMoCam(inSlowMo);
    }

    this.player.tickPowerUps(dt);
    this.integratePlayer(dt);
    this.player.updateVisual(dt, this.time.timeScale);

    this.updateParticles(dt);
    this.updateDecoys(dt);

    if (!this._gameOverLeaving && this._prevDecoyCount > 0 && this.decoys.length === 0) {
      this.purgeActiveWBC();
      this.nextWbcSpawnAllowedAfter = Math.max(
        this.nextWbcSpawnAllowedAfter,
        this.elapsed + WBC_SPAWN_CAP.afterDecoyPurgeDelaySec
      );
    }
    this._prevDecoyCount = this.decoys.length;

    this.updateEntities(dt);

    this.handleCollisions();
    if (this._gameOverLeaving) return;

    const multRaw = this.player.scoreMultiplier();
    const mult = Number.isFinite(multRaw) ? multRaw : 1;
    const gr = SCORE.growthRateMul;
    const survivalGain = SCORE.survivalPerSecond * dt * mult * gr;
    const growthGain =
      this.player.growthStacks * SCORE.growthBonusPerStack * 0.22 * dt * mult * gr;
    if (Number.isFinite(survivalGain) && Number.isFinite(growthGain)) {
      this.breakdown.survival += survivalGain;
      this.breakdown.growth += growthGain;
      this.score += survivalGain + growthGain;
    }

    const fpsDenom =
      typeof deltaMs === 'number' && Number.isFinite(deltaMs) && deltaMs > 0 ? deltaMs : 16;
    const rawFps = 1000 / fpsDenom;
    this._fpsSmooth = Phaser.Math.Linear(this._fpsSmooth, rawFps, 0.14);

    this.tickSpawner(dt, minutes, this._fpsSmooth);

    this.ui.updateHud(
      this.score,
      this.player.radius,
      this.player.shieldStages,
      SHIELD.maxStages,
      this.cloneCooldownRemaining,
      this.player.radius >= DECOY.radiusThreshold,
      this.player.speedBoostTimer,
      this.player.invisTimer,
      this.slowMoTimer
    );

    const fps = rawFps;
    this.ui.updateFps(debugState.enabled, fps);
    this.ui.drawHitboxes(debugState.enabled, this.player, this.pools);

    if (debugState.enabled) {
      const now = this.time.now;
      if (now - debugState.lastLogMs > DEBUG.spawnLogIntervalSec * 1000) {
        debugState.lastLogMs = now;
        const interval = Math.max(
          DIFFICULTY.spawnIntervalMin,
          SPAWN_ATTEMPT_BASE_INTERVAL / this.difficulty
        );
        console.log('[VIRUTOPIA debug]', {
          difficulty: Number(this.difficulty.toFixed(3)),
          flow: Number(this.flowSpeed.toFixed(2)),
          spawnInterval: Number(interval.toFixed(3)),
          fpsSmooth: Number(this._fpsSmooth.toFixed(1)),
          active: this.pools.totalGameplayActive(),
          particles: this.pools.totalParticles(),
          spawnStats: { ...this.spawner.stats },
          godMode: debugState.godMode,
        });
        this.spawner.stats.spawned = 0;
        this.spawner.stats.attempted = 0;
      }
    }
  }

  integratePlayer(dt) {
    const p = this.player;
    const sm = p.speedMultiplier();

    let ix = 0;
    let iy = 0;
    if (this.cursors.left.isDown || this.keys.A.isDown) ix -= 1;
    if (this.cursors.right.isDown || this.keys.D.isDown) ix += 1;
    if (this.cursors.up.isDown || this.keys.W.isDown) iy -= 1;
    if (this.cursors.down.isDown || this.keys.S.isDown) iy += 1;

    if (ix !== 0 || iy !== 0) {
      const len = Math.sqrt(ix * ix + iy * iy) || 1;
      ix /= len;
      iy /= len;
    }

    p.velocity.x += ix * PLAYER.accel * dt * sm;
    p.velocity.y += iy * PLAYER.accel * dt * sm;

    const maxX = PLAYER.maxSpeedX * sm;
    const maxY = PLAYER.maxSpeedY * sm;
    p.velocity.x = Phaser.Math.Clamp(p.velocity.x, -maxX, maxX);
    p.velocity.y = Phaser.Math.Clamp(p.velocity.y, -maxY, maxY);

    const drag = Math.exp(-PLAYER.drag * dt);
    p.velocity.x *= drag;
    p.velocity.y *= drag;

    p.sprite.x += p.velocity.x * dt;
    p.sprite.y += p.velocity.y * dt;
    p.sprite.x -= this.flowSpeed * PLAYER.flowResistance * dt;

    p.clampInBounds();
  }

  destroyDecoys() {
    for (const d of this.decoys) {
      if (d.sprite?.scene) d.sprite.destroy();
    }
    this.decoys.length = 0;
  }

  tryActivateClonePower() {
    if (this.decoys.length > 0) return;
    if (this.cloneCooldownRemaining > 0) return;
    if (this.player.radius < DECOY.radiusThreshold) return;

    sfxPower();
    const n = DECOY.cloneCount;
    const px = this.player.x;
    const py = this.player.y;
    const bx = this.player.sprite.scaleX * 0.88;
    const by = this.player.sprite.scaleY * 0.88;
    for (let i = 0; i < n; i++) {
      const spr = this.add.image(
        px + 18 + i * 10,
        py + (i - (n - 1) / 2) * 26,
        'game-atlas',
        Frames.PLAYER
      );
      spr.setDepth(97);
      spr.setScale(bx, by);
      spr.setTint(0x9bffbb);
      this.decoys.push({
        sprite: spr,
        vx: DECOY.fleeSpeedX + (Math.random() - 0.5) * 55,
        vy: (Math.random() - 0.5) * DECOY.wanderVyMax,
        baseScaleX: bx,
        baseScaleY: by,
        phase: Math.random() * Math.PI * 2,
      });
    }

    this.player.applyMassPenalty(DECOY.massCost);
    this.player.updateVisual(0, this.time.timeScale);

    this.cloneCooldownRemaining = DECOY.cooldownSec;
  }

  /** @param {number} dt */
  updateDecoys(dt) {
    const pad = 36;
    for (let i = this.decoys.length - 1; i >= 0; i--) {
      const d = this.decoys[i];
      const s = d.sprite;
      s.x += d.vx * dt;
      s.y += d.vy * dt;
      d.vy *= Math.exp(-2.4 * dt);
      d.vy += (Math.random() - 0.5) * 28 * dt;
      s.y = Phaser.Math.Clamp(s.y, pad, GAME_HEIGHT - pad);

      d.phase = advanceMembranePhase(d.phase, dt, 1.14);
      const { sx, sy } = membraneScaleXY(d.phase, 0.082, 0.034);
      s.setScale(d.baseScaleX * sx, d.baseScaleY * sy);
      s.rotation = 0.05 * Math.sin(d.phase * 0.61);

      if (s.x < -56) {
        s.destroy();
        this.decoys.splice(i, 1);
      }
    }
  }

  countActiveWBC() {
    let n = 0;
    for (const img of this.pools.wbc.iterateActive()) {
      if (img.poolData.alive) n++;
    }
    return n;
  }

  purgeActiveWBC() {
    for (const img of [...this.pools.wbc.iterateActive()]) {
      this.pools.wbc.release(img);
    }
  }

  wbcSpawnSpacingSec() {
    const m = WBC_SPAWN_CAP;
    const minutes = this.elapsed / 60;
    const ramp = Math.min(1, minutes / m.rampMinutes);
    const hi = Phaser.Math.FloatBetween(m.spawnSpacingMin, m.spawnSpacingMax);
    return Phaser.Math.Linear(hi, m.spawnSpacingFloor, ramp);
  }

  /** @param {number} minutes */
  pickSpawnKind(minutes) {
    const cap = WBC_SPAWN_CAP.hardMax;
    const wbcNow = this.countActiveWBC();

    for (let a = 0; a < 16; a++) {
      const k = this.spawner.pickKind(this.difficulty, minutes);
      if (k !== EntityKind.WBC) return k;

      if (wbcNow >= cap) continue;

      if (this.elapsed < this.nextWbcSpawnAllowedAfter) continue;

      return k;
    }
    return EntityKind.COLLECTIBLE;
  }

  updateParticles(dt) {
    const imgs = [...this.pools.particles.iterateActive()];
    for (const img of imgs) {
      if (!img.poolData.alive) continue;
      Move.driftParticle(img, dt, img.poolData.speed ?? PARTICLES.speedMin);
      if (Move.offscreenLeft(img, 8, 10)) {
        img.x = GAME_WIDTH + 20 + Math.random() * 120;
        img.y = Math.random() * GAME_HEIGHT;
        img.poolData.speed = PARTICLES.speedMin + Math.random() * (PARTICLES.speedMax - PARTICLES.speedMin);
      }
    }
  }

  updateEntities(dt) {
    const flow = this.flowSpeed;
    const diff = this.difficulty;
    const px = this.player.x;
    const py = this.player.y;

    const decoyPts =
      this.decoys.length > 0
        ? this.decoys.map((d) => ({ x: d.sprite.x, y: d.sprite.y }))
        : undefined;
    const wbcSeek = { playerX: px, playerY: py, decoys: decoyPts };

    const step = (pool, fn) => {
      const imgs = [...pool.iterateActive()];
      for (const img of imgs) {
        if (!img.poolData.alive) continue;
        fn(img);
        Move.clampVertical(img, img.poolData.radius ?? 8);
        Move.recycleIfOffworld(img, img.poolData.radius ?? 8, this.pools);
      }
    };

    step(this.pools.collectibles, (img) => Move.driftCollectible(img, dt, flow));
    step(this.pools.wbc, (img) => Move.driftWBC(img, dt, flow, wbcSeek, diff));
    for (const img of this.pools.wbc.iterateActive()) {
      if (!img.poolData.alive) continue;
      const mul = typeof img.poolData.seekMul === 'number' ? img.poolData.seekMul : 1;
      img.poolData.wobblePhase = advanceMembranePhase(img.poolData.wobblePhase, dt, 1.05 + mul * 0.25);
      const { sx, sy } = membraneScaleXY(img.poolData.wobblePhase, 0.124, 0.052);
      img.setScale(sx, sy);
      img.rotation = wbcMembraneRotation(img.poolData.wobblePhase, mul);
    }

    step(this.pools.antibodies, (img) => Move.driftAntibody(img, dt, flow));
    step(this.pools.platelets, (img) => Move.driftPlatelet(img, dt, flow, diff));

    const powerStep = (pool) =>
      step(pool, (img) => Move.driftPowerUp(img, dt, flow));

    powerStep(this.pools.powerShield);
    powerStep(this.pools.powerSpeed);
    powerStep(this.pools.powerInvis);
    powerStep(this.pools.powerSlow);
  }

  /**
   * @param {number} dt
   * @param {number} minutes
   * @param {number} fpsSmooth
   */
  tickSpawner(dt, minutes, fpsSmooth) {
    if (this.pools.totalGameplayActive() >= ENTITY_CAP) return;

    const low = fpsSmooth < PERFORMANCE.lowFpsThreshold;
    const interval =
      Math.max(
        DIFFICULTY.spawnIntervalMin,
        SPAWN_ATTEMPT_BASE_INTERVAL / this.difficulty
      ) * (low ? PERFORMANCE.spawnIntervalLowFpsMult : 1);

    const maxBurst = low ? PERFORMANCE.maxSpawnBurstWhenLowFps : PERFORMANCE.maxSpawnBurstNormal;

    this.spawnAccumulator += dt;
    let bursts = 0;
    while (
      this.spawnAccumulator >= interval &&
      this.pools.totalGameplayActive() < ENTITY_CAP &&
      bursts < maxBurst
    ) {
      this.spawnAccumulator -= interval;
      this.spawner.stats.attempted++;
      const kind = this.pickSpawnKind(minutes);
      if (this.spawner.trySpawn(kind, this.player, this.difficulty)) {
        this.spawner.stats.spawned++;
        if (kind === EntityKind.WBC) {
          this.nextWbcSpawnAllowedAfter = this.elapsed + this.wbcSpawnSpacingSec();
        }
      }
      bursts++;
    }
  }

  /**
   * @param {{ score: number; breakdown: object; elapsed: number }} payload
   */
  queueGameOver(payload) {
    if (this._gameOverLeaving) return;
    this._gameOverLeaving = true;

    this.destroyDecoys();

    this.registry.set('finalScore', payload.score);
    this.registry.set('lastRun', {
      breakdown: payload.breakdown,
      elapsed: payload.elapsed,
    });
    this.time.timeScale = 1;
    this.applySlowMoCam(false);
    if (this.ui) this.ui.setSlowMoFx(false);

    const handoff = {
      score: payload.score,
      breakdown: { ...payload.breakdown },
      elapsed: payload.elapsed,
    };

    const prevTd = this.game.registry.get('gameOverOverlayTeardown');
    if (typeof prevTd === 'function') {
      prevTd();
    }
    const overlayTeardown = mountGameOverDom(this.game, handoff);
    this.game.registry.set('gameOverOverlayTeardown', overlayTeardown);

    this.scene.start('GameOver', handoff);
  }

  handleCollisions() {
    const p = this.player;
    const pr = p.radius;

    const enemyKinds = new Set([
      EntityKind.WBC,
      EntityKind.ANTIBODY,
      EntityKind.PLATELET,
    ]);

    let died = false;

    /** @param {import('../systems/ObjectPool.js').ObjectPool} pool */
    const scan = (pool) => {
      if (died) return;
      const imgs = [...pool.iterateActive()];
      for (const img of imgs) {
        if (!img.poolData.alive) continue;
        const kind = img.poolData.kind;
        const r = img.poolData.radius ?? ENTITY_RADIUS.collectible;
        const playerR = enemyKinds.has(kind)
          ? PLAYER_BASE_RADIUS +
            Math.max(0, pr - PLAYER_BASE_RADIUS) * HITBOX.threatGrowthScale
          : pr;
        if (!circlesOverlap(p.x, p.y, playerR, img.x, img.y, r)) continue;

        if (kind === EntityKind.COLLECTIBLE) {
          p.applyGrowth();
          const pickupGain = SCORE.collectibleBase * p.scoreMultiplier() * SCORE.growthRateMul;
          this.breakdown.pickups += pickupGain;
          this.score += pickupGain;
          sfxCollect();
          if (this.cameras.main.shake) {
            this.cameras.main.shake(FEEL.shakeGrowthMs, FEEL.shakeGrowth);
          }
          p.flashHit(0.05);
          pool.release(img);
          continue;
        }

        if (
          kind === EntityKind.POWER_SHIELD ||
          kind === EntityKind.POWER_SPEED ||
          kind === EntityKind.POWER_INVIS ||
          kind === EntityKind.POWER_SLOW
        ) {
          const dur = img.poolData.powerDuration ?? 4;
          if (kind === EntityKind.POWER_SHIELD) p.addShieldStages(1);
          if (kind === EntityKind.POWER_SPEED) p.speedBoostTimer = Math.max(p.speedBoostTimer, dur);
          if (kind === EntityKind.POWER_INVIS) p.invisTimer = Math.max(p.invisTimer, dur);
          if (kind === EntityKind.POWER_SLOW) this.slowMoTimer = Math.max(this.slowMoTimer, dur);
          sfxPower();
          pool.release(img);
          continue;
        }

        if (enemyKinds.has(kind)) {
          if (debugState.godMode) {
            pool.release(img);
            continue;
          }

          if (p.isInvisible()) continue;

          if (kind === EntityKind.WBC && this.decoys.length > 0) {
            continue;
          }

          if (p.isShielded()) {
            p.consumeShieldHit();
            sfxPower();
            if (this.cameras.main.shake) {
              this.cameras.main.shake(FEEL.shakeShieldMs, FEEL.shakeShield);
            }
            p.flashHit(0.095);
            this.ui.flashWhite(64, 0.74);
            pool.release(img);
            continue;
          }

          sfxHit();
          died = true;
          this.queueGameOver({
            score: this.score,
            breakdown: { ...this.breakdown },
            elapsed: this.elapsed,
          });
          return;
        }
      }
    };

    for (const pool of this.pools.gamePools) {
      if (died) break;
      scan(pool);
    }
  }
}
